import { Link } from 'react-router-dom'
import { useState } from 'react'

interface FeatureCard {
  title: string
  description: string
  icon: string
  link?: string
  color: string
  category: 'core' | 'advanced' | 'admin'
}

const features: FeatureCard[] = [
  {
    title: 'Time Tracking',
    description: 'Start, stop, and manage your time entries with ease. Track your work hours and project time efficiently. Resume completed entries with one click.',
    icon: '⏱️',
    link: '/entries',
    color: 'from-blue-500 to-blue-600',
    category: 'core'
  },
  {
    title: 'Project Management',
    description: 'Organize your work by projects. Create, edit, and manage projects with custom colors and settings.',
    icon: '📁',
    link: '/projects',
    color: 'from-green-500 to-green-600',
    category: 'core'
  },
  {
    title: 'Calendar View',
    description: 'Visualize your time entries in a calendar format. See your schedule and plan your work effectively.',
    icon: '📅',
    link: '/calendar',
    color: 'from-purple-500 to-purple-600',
    category: 'core'
  },
  {
    title: 'Reports & Analytics',
    description: 'Generate detailed reports and analyze your time data. Export data and gain insights into your productivity.',
    icon: '📊',
    link: '/reporting',
    color: 'from-orange-500 to-orange-600',
    category: 'core'
  },
  {
    title: 'Dashboard Overview',
    description: 'Get a quick overview of your recent activity, running timers, and key metrics at a glance.',
    icon: '🏠',
    link: '/',
    color: 'from-indigo-500 to-indigo-600',
    category: 'core'
  },
  {
    title: 'Billable Projects',
    description: 'Mark projects as billable and track billable hours separately for client invoicing.',
    icon: '💰',
    color: 'from-emerald-500 to-emerald-600',
    category: 'advanced'
  },
  {
    title: 'Shared Projects',
    description: 'Collaborate on projects with team members. Share project access and track time across multiple users.',
    icon: '🤝',
    color: 'from-teal-500 to-teal-600',
    category: 'advanced'
  },
  {
    title: 'Project Budget Management',
    description: 'Track project budgets with monetary and hourly limits. Visualize budget usage and compliance.',
    icon: '📈',
    color: 'from-violet-500 to-violet-600',
    category: 'advanced'
  },
  {
    title: 'Holiday System',
    description: 'Automatic holiday detection and display in calendar. Configure country and regional holiday preferences.',
    icon: '🎉',
    link: '/settings',
    color: 'from-red-500 to-red-600',
    category: 'advanced'
  },
  {
    title: 'Data Export',
    description: 'Export your time tracking data in various formats. Generate reports for clients, accounting, or analysis.',
    icon: '📤',
    color: 'from-yellow-500 to-yellow-600',
    category: 'advanced'
  },
  {
    title: 'Resume Time Entries',
    description: 'Quickly resume any completed time entry with the same project, location, and description. Smart visibility ensures it only appears when no timer is running.',
    icon: '▶️',
    link: '/entries',
    color: 'from-green-500 to-green-600',
    category: 'advanced'
  },
  {
    title: 'Role Management',
    description: 'Admin functionality to manage user roles and permissions. Control access levels and user capabilities.',
    icon: '👥',
    link: '/role-management',
    color: 'from-pink-500 to-pink-600',
    category: 'admin'
  },
  {
    title: 'Entry Management',
    description: 'Advanced entry management for admins and readers. Edit, delete, and manage time entries across users.',
    icon: '✏️',
    link: '/entry-management',
    color: 'from-cyan-500 to-cyan-600',
    category: 'admin'
  },
  {
    title: 'User Settings',
    description: 'Customize your preferences, holiday settings, and personal configuration for optimal experience.',
    icon: '⚙️',
    link: '/settings',
    color: 'from-gray-500 to-gray-600',
    category: 'advanced'
  },
  {
    title: 'SSO Authentication',
    description: 'Secure Single Sign-On with OIDC providers. Seamless login with Azure AD and other enterprise systems.',
    icon: '🔐',
    color: 'from-slate-500 to-slate-600',
    category: 'admin'
  },
  {
    title: 'Advanced Date Selection',
    description: 'Intuitive calendar picker with integrated view switching and time selection. Smart date range picker for precise time entry management.',
    icon: '📅',
    color: 'from-blue-500 to-blue-600',
    category: 'advanced'
  },
  {
    title: 'Searchable Dropdowns',
    description: 'Enhanced dropdown components with integrated search functionality. Find options quickly by typing directly in dropdown fields.',
    icon: '🔍',
    color: 'from-indigo-500 to-indigo-600',
    category: 'advanced'
  },
  {
    title: 'Enhanced Pagination',
    description: 'Advanced pagination system with configurable page sizes and smart navigation. Optimized for large datasets.',
    icon: '📄',
    color: 'from-purple-500 to-purple-600',
    category: 'advanced'
  },
  {
    title: 'Multi-Project Reporting',
    description: 'Select multiple projects for combined reporting and analysis. Generate comprehensive reports across multiple projects.',
    icon: '📊',
    link: '/reporting',
    color: 'from-orange-500 to-orange-600',
    category: 'advanced'
  },
  {
    title: 'Location-Specific Rates',
    description: 'Set different hourly rates for different locations within projects. Flexible pricing for on-site vs remote work.',
    icon: '📍',
    color: 'from-emerald-500 to-emerald-600',
    category: 'advanced'
  },
  {
    title: 'Confirmation Dialogs',
    description: 'Modern confirmation dialogs with contextual messaging and visual feedback. Enhanced user experience for critical actions.',
    icon: '⚠️',
    color: 'from-red-500 to-red-600',
    category: 'advanced'
  },
  {
    title: 'Real-time Duration Sync',
    description: 'Live duration updates and real-time synchronization between dashboard and forms. Keep tracking data current.',
    icon: '🔄',
    color: 'from-green-500 to-green-600',
    category: 'advanced'
  },
  {
    title: 'Enhanced Project Data Management',
    description: 'Complete access to all projects including archived ones. Consistent project data across all components with improved lookup and display.',
    icon: '🗂️',
    color: 'from-amber-500 to-amber-600',
    category: 'advanced'
  },
  {
    title: 'Auto-location Selection',
    description: 'Automatic location selection when choosing projects in entry forms. Streamlined workflow for faster time entry creation.',
    icon: '🎯',
    color: 'from-lime-500 to-lime-600',
    category: 'advanced'
  }
]

export default function Information() {
  const [activeCategory, setActiveCategory] = useState<'all' | 'core' | 'advanced' | 'admin'>('all')

  const filteredFeatures = activeCategory === 'all' 
    ? features 
    : features.filter(feature => feature.category === activeCategory)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-4">
            <span className="text-6xl">⏱️</span>
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Promptime
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Your comprehensive time tracking solution designed to help you manage projects, 
            track time efficiently, and boost your productivity with enterprise-grade features.
          </p>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { key: 'all', label: 'All Features', count: features.length },
              { key: 'core', label: 'Core Features', count: features.filter(f => f.category === 'core').length },
              { key: 'advanced', label: 'Advanced', count: features.filter(f => f.category === 'advanced').length },
              { key: 'admin', label: 'Admin Tools', count: features.filter(f => f.category === 'admin').length }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key as 'all' | 'core' | 'advanced' | 'admin')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeCategory === key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredFeatures.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 ease-out transform hover:-translate-y-2 border border-gray-100 overflow-hidden"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {feature.link ? (
                  <Link to={feature.link} className="block p-6 h-full relative z-10">
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                            {feature.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            feature.category === 'core' ? 'bg-blue-100 text-blue-700' :
                            feature.category === 'advanced' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {feature.category}
                          </span>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                        <div className="mt-3 text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform duration-200">
                          Learn more →
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="p-6 h-full relative z-10">
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {feature.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            feature.category === 'core' ? 'bg-blue-100 text-blue-700' :
                            feature.category === 'advanced' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {feature.category}
                          </span>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Tips Section */}
        <div className="bg-gradient-to-r from-blue-50/40 to-indigo-50/40 backdrop-blur-sm rounded-2xl shadow-lg border border-blue-100/30 p-8 mb-16 mx-4 mt-16">
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-900">
            💡 Quick Tips to Get Started
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { tip: "Use the Dashboard to get a quick overview of your recent activity and running timers.", icon: "🏠" },
              { tip: "Create Projects to organize your work and track time by specific tasks or clients.", icon: "📁" },
              { tip: "Use the Calendar view to see your time entries in a visual format and plan your schedule.", icon: "📅" },
              { tip: "Generate Reports to analyze your productivity and export data for invoicing or analysis.", icon: "📊" },
              { tip: "Mark projects as Billable to track client work separately and generate invoices easily.", icon: "💰" },
              { tip: "Use Entry Management (admin/reader) to manage time entries across all users and projects.", icon: "✏️" },
              { tip: "Access Role Management (admin only) to control user permissions and access levels.", icon: "👥" },
              { tip: "Export data in Excel/CSV formats for external analysis, accounting, or client reporting.", icon: "📤" },
              { tip: "Use Shared Projects to collaborate with team members and track time across multiple users.", icon: "🤝" },
              { tip: "Configure Holiday Settings in User Settings to display holidays in your calendar view.", icon: "🎉" },
              { tip: "Set up Project Budgets to track monetary or hourly limits and monitor budget compliance.", icon: "📈" },
              { tip: "Use SSO Authentication for secure enterprise login with Azure AD or other OIDC providers.", icon: "🔐" },
              { tip: "Use the advanced date picker for precise time entry management with calendar integration.", icon: "📅" },
              { tip: "Search dropdowns by typing directly in the field for quick option selection.", icon: "🔍" },
              { tip: "Configure location-specific hourly rates for different work environments.", icon: "📍" },
              { tip: "Use multi-project selection in reporting for comprehensive analysis across projects.", icon: "📊" },
              { tip: "Resume completed time entries with one click to continue working on the same task.", icon: "▶️" },
              { tip: "Monitor real-time duration updates for accurate time tracking across all interfaces.", icon: "🔄" },
              { tip: "Access archived projects in dropdowns and entry forms for complete project visibility.", icon: "🗂️" },
              { tip: "Let the system auto-select the first location when choosing a project for faster entry creation.", icon: "🎯" }
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white/50 backdrop-blur-sm rounded-lg p-4 border border-white/40 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex-shrink-0 text-2xl">{item.icon}</div>
                <p className="text-sm leading-relaxed text-gray-700">{item.tip}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
} 